// FILE: apps/admin/src/components/GlobalSearch.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Cmd+K / Ctrl+K global search omnibox across users, orders, payments, and support tickets.
//   SCOPE: GlobalSearch component with keyboard shortcut and instant entity navigation.
//   DEPENDS: ../api.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   GlobalSearch - Topbar global search input and modal dropdown
// END_MODULE_MAP

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { api, rub } from '../api.js';
import { AdminIcon } from './Icons.js';
import { resolveAdminUserDisplayName, UserAvatar } from './UserAvatar.js';

interface SearchResponse {
  query: string;
  users: Array<{
    id: string;
    telegramId: number;
    username: string | null;
    firstName: string | null;
    customName?: string | null;
    photoUrl?: string | null;
    customPhotoUrl?: string | null;
    isVerified?: boolean;
    balanceKopecks: number;
    xp: number;
    isBanned: boolean;
  }>;
  orders: Array<{
    id: string;
    userId: string;
    telegramId: number | null;
    username: string | null;
    product: string;
    qty: number;
    status: string;
    expectedAmountKopecks: number;
    createdAt: string;
  }>;
  payments: Array<{
    id: string;
    externalId: string;
    amountKopecks: number;
    senderName: string | null;
    matchedOrderId: string | null;
    receivedAt: string;
  }>;
  tickets: Array<{
    id: string;
    ticketNumber: number;
    telegramId: number;
    username: string | null;
    category: string;
    subject: string;
    state: string;
  }>;
}

// START_CONTRACT: GlobalSearch
//   PURPOSE: Render topbar omnibox with Ctrl+K / Cmd+K shortcut for instant search across all platform entities.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries /admin/search and navigates via react-router
//   LINKS: M-ADMIN
// END_CONTRACT: GlobalSearch
export function GlobalSearch() {
  // START_BLOCK_GLOBAL_SEARCH
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 20);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const trimmed = q.trim();
  const { data, isFetching } = useQuery({
    queryKey: ['admin-search', trimmed],
    queryFn: () => api.get<SearchResponse>(`/admin/search?q=${encodeURIComponent(trimmed)}`),
    enabled: open && trimmed.length >= 2,
  });

  const go = (path: string) => {
    setOpen(false);
    setQ('');
    navigate(path);
  };

  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  const shortcutLabel = isMac ? 'Cmd K' : 'Ctrl K';

  const modalNode =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div className="modal-backdrop omnibox-backdrop" onClick={() => setOpen(false)}>
            <div className="omnibox-modal card" onClick={(e) => e.stopPropagation()}>
              <div className="row" style={{ marginBottom: 12 }}>
                <input
                  ref={inputRef}
                  autoFocus
                  className="omnibox-input"
                  placeholder="Введите @username, Telegram ID, UUID заказа или сумму…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <button type="button" className="btn ghost sm" onClick={() => setOpen(false)}>
                  Esc
                </button>
              </div>

              {trimmed.length < 2 ? (
                <div className="muted" style={{ padding: '10px 4px' }}>
                  Введите минимум 2 символа для глобального поиска по базе Stars Fish
                </div>
              ) : isFetching ? (
                <div className="muted" style={{ padding: '10px 4px' }}>
                  Поиск по базе…
                </div>
              ) : (
                <div className="omnibox-results">
                  {Boolean(data?.users.length) && (
                    <div className="omnibox-section">
                      <div className="omnibox-title">Пользователи ({data!.users.length})</div>
                      {data!.users.map((u) => {
                        const dn = resolveAdminUserDisplayName(u);
                        return (
                          <div
                            key={u.id}
                            className="omnibox-item"
                            onClick={() => go(`/users?userId=${encodeURIComponent(u.id)}`)}
                          >
                            <div className="user-cell">
                              <UserAvatar
                                photoUrl={u.photoUrl}
                                customPhotoUrl={u.customPhotoUrl}
                                name={dn.primary}
                                size={30}
                              />
                              <div className="user-cell-name-row">
                                <b>{dn.primary}</b>
                                {u.isVerified ? <AdminIcon name="verified" size={14} /> : null}
                                {dn.originalTgName ? (
                                  <span className="tg-orig-tag">TG: {dn.originalTgName}</span>
                                ) : null}
                                {u.username ? <span className="muted">@{u.username}</span> : null}
                                <span className="mono muted">#{u.telegramId}</span>
                              </div>
                            </div>
                            <div className="mono">
                              {rub(u.balanceKopecks)} ₽ · {u.xp} XP
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {Boolean(data?.orders.length) && (
                    <div className="omnibox-section">
                      <div className="omnibox-title">Заказы ({data!.orders.length})</div>
                      {data!.orders.map((o) => (
                        <div
                          key={o.id}
                          className="omnibox-item"
                          onClick={() => go(`/orders?q=${encodeURIComponent(o.id.slice(0, 8))}`)}
                        >
                          <div>
                            <span className="mono">#{o.id.slice(0, 8).toUpperCase()}</span> ·{' '}
                            {o.product} ({o.qty}) ·{' '}
                            <span className={'chip ' + o.status}>{o.status}</span>
                          </div>
                          <div className="mono">{rub(o.expectedAmountKopecks)} ₽</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {Boolean(data?.payments.length) && (
                    <div className="omnibox-section">
                      <div className="omnibox-title">Платежи СБП ({data!.payments.length})</div>
                      {data!.payments.map((p) => (
                        <div
                          key={p.id}
                          className="omnibox-item"
                          onClick={() => go('/payments')}
                        >
                          <div>
                            <b>{p.senderName ?? 'Отправитель'}</b> ·{' '}
                            <span className="mono muted">{p.externalId}</span>
                          </div>
                          <div className="mono">{rub(p.amountKopecks)} ₽</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {Boolean(data?.tickets.length) && (
                    <div className="omnibox-section">
                      <div className="omnibox-title">Тикеты поддержки ({data!.tickets.length})</div>
                      {data!.tickets.map((t) => (
                        <div
                          key={t.id}
                          className="omnibox-item"
                          onClick={() => go(`/support?ticketId=${encodeURIComponent(t.id)}`)}
                        >
                          <div>
                            <span className="mono">#{t.ticketNumber}</span> · <b>{t.subject}</b>
                          </div>
                          <span className="chip">{t.state}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {!data?.users.length &&
                    !data?.orders.length &&
                    !data?.payments.length &&
                    !data?.tickets.length && (
                      <div className="muted" style={{ padding: '14px 4px', textAlign: 'center' }}>
                        Ничего не найдено по запросу «{trimmed}»
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="omnibox-wrap">
      <div
        className="omnibox-trigger"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 20);
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="omnibox-label-desktop">Поиск по заказам, пользователям, ID...</span>
          <span className="omnibox-label-mobile">Поиск...</span>
        </span>
        <kbd className="omnibox-kbd">{shortcutLabel}</kbd>
      </div>
      {modalNode}
    </div>
  );
  // END_BLOCK_GLOBAL_SEARCH
}
